import React, { createContext, useContext, useCallback, useRef, useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import { setLogoutState } from '../utils/toastManager';
import { setGlobalToastBlocking } from '../utils/globalErrorHandler';
import { AuthHelper } from '../utils/authHelper';

interface SessionContextType {
  handleSessionExpiry: () => void;
  handleUnauthorized: () => void;
  isLoggingOut: boolean;
  showLogoutLoader: boolean;
}

const SessionContext = createContext<SessionContextType | undefined>(undefined);

export const useSession = () => {
  const context = useContext(SessionContext);
  if (context === undefined) {
    throw new Error('useSession must be used within a SessionProvider');
  }
  return context;
};

interface SessionProviderProps {
  children: React.ReactNode;
}

export const SessionProvider: React.FC<SessionProviderProps> = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const isLoggingOutRef = useRef(false);
  const sessionExpiryToastId = useRef<string | null>(null);
  const unauthorizedToastId = useRef<string | null>(null);
  const [showLogoutLoader, setShowLogoutLoader] = useState(false);

  // Initialize clean state on app load
  useEffect(() => {
    // Clear any persistent loading state that might be cached
    setShowLogoutLoader(false);
    isLoggingOutRef.current = false;
    sessionExpiryToastId.current = null;
    unauthorizedToastId.current = null;
    setLogoutState(false);
    setGlobalToastBlocking(false);
    
    // Clear any loading state from localStorage/sessionStorage
    localStorage.removeItem('sessionLoading');
    sessionStorage.removeItem('sessionLoading');
    
    // Clear any persisting DOM elements that might be stuck
    setTimeout(() => {
      toast.dismiss(); // Clear any stuck toasts
      // Force clear any loading overlays that might be stuck in DOM
      const loadingOverlays = document.querySelectorAll('[style*="position: fixed"]');
      loadingOverlays.forEach(overlay => {
        const el = overlay as HTMLElement;
        if (el.style.zIndex === '9999' && el.textContent?.includes('Session expired')) {
          el.remove();
        }
      });
    }, 100);
  }, []); // Run once on mount

  // Force clear loading state on route changes
  useEffect(() => {
    // Clear loading state whenever route changes
    if (location.pathname === '/') {
      // On login page, ensure no loading state
      setShowLogoutLoader(false);
      isLoggingOutRef.current = false;
      sessionExpiryToastId.current = null;
      unauthorizedToastId.current = null;
      setLogoutState(false);
      setGlobalToastBlocking(false);
      localStorage.removeItem('sessionLoading');
      sessionStorage.removeItem('sessionLoading');
    }
  }, [location.pathname]);

  const performLogout = useCallback(() => {
    if (isLoggingOutRef.current) return; // Prevent multiple simultaneous logouts
    
    // IMMEDIATELY clear loading state and DOM
    setShowLogoutLoader(false);
    isLoggingOutRef.current = true;
    
    // AGGRESSIVE DOM cleanup - remove loading overlays immediately
    const forceRemoveLoadingOverlays = () => {
      // Find all potential loading overlays
      const overlays = document.querySelectorAll('[style*="position: fixed"], [style*="position:fixed"]');
      overlays.forEach(overlay => {
        const el = overlay as HTMLElement;
        if (el.style.zIndex === '9999' || 
            el.textContent?.includes('Session expired') || 
            el.textContent?.includes('Redirecting to login') ||
            (el.style.position === 'fixed' && el.style.backgroundColor?.includes('rgba(255, 255, 255'))) {
          el.remove();
        }
      });
      
      // Also remove any divs with loading content
      const loadingDivs = document.querySelectorAll('div');
      loadingDivs.forEach(div => {
        if (div.textContent?.includes('Session expired') || div.textContent?.includes('Redirecting to login')) {
          div.remove();
        }
      });
    };
    
    // Clear overlays immediately and repeatedly
    forceRemoveLoadingOverlays();
    const cleanupInterval = setInterval(forceRemoveLoadingOverlays, 50);
    
    // ULTRA AGGRESSIVE toast blocking
    setLogoutState(true);
    setGlobalToastBlocking(true);
    toast.dismiss();
    
    // Clear all authentication data using AuthHelper
    AuthHelper.clearAuthData();
    
    // Reset all flags
    sessionExpiryToastId.current = null;
    unauthorizedToastId.current = null;
    setLogoutState(false);
    setGlobalToastBlocking(false);
    
    // Clean up interval and redirect
    setTimeout(() => {
      clearInterval(cleanupInterval);
      forceRemoveLoadingOverlays(); // One final cleanup
      isLoggingOutRef.current = false;
      
      // Use React Router navigation instead of window.location
      navigate('/', { replace: true });
      
      // Also force page refresh as backup
      setTimeout(() => {
        window.location.reload();
      }, 100);
    }, 100);
  }, [navigate]);

  const handleSessionExpiry = useCallback(() => {
    if (isLoggingOutRef.current) return;
    
    // If there's already a session expiry toast, don't show another one
    if (sessionExpiryToastId.current) return;
    
    // Set logging out flag immediately
    isLoggingOutRef.current = true;
    sessionExpiryToastId.current = 'logging-out';
    
    // Clear auth data immediately
    AuthHelper.clearAuthData();
    
    // Show brief loading message
    toast.info("Session expired. Redirecting to login...", { autoClose: 1000 });
    
    // Navigate immediately - don't wait
    navigate('/', { replace: true });
    
    // Reset the logout flag after navigation to prevent loops
    setTimeout(() => {
      isLoggingOutRef.current = false;
      sessionExpiryToastId.current = null;
    }, 2000);
  }, [navigate]);

  const handleUnauthorized = useCallback(() => {
    if (isLoggingOutRef.current) return;
    
    // If there's already an unauthorized toast, don't show another one
    if (unauthorizedToastId.current) return;
    
    // Set logging out flag immediately
    isLoggingOutRef.current = true;
    unauthorizedToastId.current = 'logging-out';
    
    // Clear auth data immediately
    AuthHelper.clearAuthData();
    
    // Show brief loading message
    toast.info("Session expired. Redirecting to login...", { autoClose: 1000 });
    
    // Navigate immediately - don't wait
    navigate('/', { replace: true });
    
    // Reset the logout flag after navigation to prevent loops
    setTimeout(() => {
      isLoggingOutRef.current = false;
      unauthorizedToastId.current = null;
    }, 2000);
  }, [navigate]);

  const value = {
    handleSessionExpiry,
    handleUnauthorized,
    isLoggingOut: isLoggingOutRef.current,
    showLogoutLoader,
  };

  return (
    <SessionContext.Provider value={value}>
      {children}
      {/* REMOVED PERSISTENT LOADING OVERLAY - Using toast instead */}
    </SessionContext.Provider>
  );
};